import { capitalize } from 'lodash';
import { Building2 } from 'lucide-react';
import React from 'react';
import { ExternalEvent } from 'v5/api/external-event/external-event.api';
import { Badge } from 'v5/design-system/Badge';
import { cn } from 'v5/platform/dom/cn';
import { WithServices } from 'v5/platform/ts/services';
import { WithToastService } from '../../front/ToastService/ToastService';
import { WithEventService } from '../EventService/EventService';
import type { BookingSidebarInitialTab } from 'v5/services/front/UserFlowService/UserFlowService';
import { VisitorManagementService } from './VisitorManagementService';

// Single source of truth: set to false to show not_eligible items in toast alerts
const IGNORE_NOT_ELIGIBLE = true;

export function createVisitorManagementService(
  config: WithServices<[WithToastService, WithEventService]> & {
    getCurrentAccountId: () => string | null; // current logged-in account id
    openBooking?: (
      bookingId: number,
      options?: { initialTab?: BookingSidebarInitialTab }
    ) => void;
  }
): VisitorManagementService {
  const handleEvent = (event: ExternalEvent) => {
    if (event.type !== 'VISITOR_MANAGEMENT_PROCESSED') return;
    const data = event.payload;

    const currentAccountId = config.getCurrentAccountId();
    if (currentAccountId == null) return;

    const isTriggeredByCurrentUser =
      data.triggeredByAccountId === currentAccountId;
    // Only show alerts to the user who triggered the sync, not to other users
    if (!isTriggeredByCurrentUser) return;

    // V3: New status-based counting with discriminated unions
    const successCount = data.vendors.filter(
      v => v.status === 'success'
    ).length;
    const preparationErrorCount = data.vendors.filter(
      v => v.status === 'preparation_error'
    ).length;
    const apiErrorCount = data.vendors.filter(
      v => v.status === 'api_error'
    ).length;
    const notEligibleCount = data.vendors.filter(
      v => v.status === 'not_eligible'
    ).length;

    // Calculate effective items to display (not_eligible excluded when IGNORE_NOT_ELIGIBLE is true)
    const effectiveItemsCount =
      successCount +
      preparationErrorCount +
      apiErrorCount +
      (IGNORE_NOT_ELIGIBLE ? 0 : notEligibleCount);

    // Log not_eligible items only when we're hiding them from the UI
    if (IGNORE_NOT_ELIGIBLE && notEligibleCount > 0) {
      console.log(
        `Visitor Management: ${notEligibleCount} vendor(s) not eligible for booking #${data.bookingId}`,
        {
          notEligibleVendors: data.vendors.filter(
            v => v.status === 'not_eligible'
          )
        }
      );
    }

    // Don't show toast if there are no effective items to display
    if (effectiveItemsCount === 0) {
      return;
    }

    // Determine sync status badge
    const hasErrors = preparationErrorCount > 0 || apiErrorCount > 0;
    const statusBadge = hasErrors ? (
      <Badge variant="danger" tone="solid" size="sm" className="gap-1">
        Out of Sync
      </Badge>
    ) : (
      <Badge variant="success" tone="solid" size="sm" className="gap-1">
        In Sync
      </Badge>
    );

    config.services.toastService.open({
      type: 'default',
      position: 'top-right',
      message: (
        <div className="flex flex-col gap-3 max-w-96">
          {/* Header */}
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-brand-600" />
              <h3 className="font-semibold text-base text-fg-default whitespace-nowrap">
                Visitor Management
              </h3>
            </div>
            {statusBadge}
            <span
              className="ml-auto font-mono text-sm font-medium text-brand-600 bg-brand-50 px-2 py-0.5 rounded whitespace-nowrap cursor-pointer hover:bg-brand-100 hover:text-brand-700 transition-colors"
              onClick={() => {
                config.openBooking?.(data.bookingId, {
                  initialTab: 'visitor-management'
                });
              }}
            >
              #{data.bookingId}
            </span>
          </div>

          {/* Vendor Results */}
          <div className="space-y-2.5">
            {data.vendors
              .filter(v => !IGNORE_NOT_ELIGIBLE || v.status !== 'not_eligible')
              .map((v, i) => (
                <div key={i} className="space-y-1.5">
                  {/* Vendor name */}
                  <div className="text-sm font-semibold text-fg-default">
                    {capitalize(v.vendor)}
                  </div>

                  {/* Operation (primary) and Status (secondary) */}
                  <div className="flex items-center gap-2">
                    {v.operation && (
                      <Badge
                        variant={
                          v.status === 'success'
                            ? v.operation === 'register'
                              ? 'success'
                              : v.operation === 'update'
                                ? 'info'
                                : v.operation === 'unregister'
                                  ? 'attention'
                                  : 'gray'
                            : 'danger'
                        }
                        tone="subtle"
                        size="sm"
                        className="font-medium"
                      >
                        {v.operation}
                      </Badge>
                    )}
                    <span className="text-xs text-fg-muted">
                      {v.status === 'success'
                        ? 'synced'
                        : v.status === 'preparation_error'
                          ? 'missing data'
                          : v.status === 'api_error'
                            ? 'error'
                            : 'not eligible'}
                    </span>
                  </div>

                  {/* Error Details or Reason */}
                  {v.status !== 'success' && v.message && (
                    <div className="text-xs font-medium text-red-600">
                      {v.message}
                    </div>
                  )}
                  {v.status === 'success' && 'reason' in v && v.reason && (
                    <div
                      className={
                        'text-xs font-medium ' +
                        (v.reason.status === 'not_eligible'
                          ? 'text-gray-600'
                          : 'text-amber-600')
                      }
                    >
                      {v.reason.message}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      )
    });
  };

  // subscribe immediately on creation
  config.services.eventService.event.addListener(handleEvent);

  return {};
}
